// =============================================================================
// ui/tabs/DeleteAccountDialog.tsx — the confirm for the one irreversible action.
// -----------------------------------------------------------------------------
// ── 🚨 IT NAMES WHAT THE OLD CLIENT'S DOES NOT ──
//
// Beta's confirm is one sentence: "Delete this account? Trades stay but become
// unassigned." True, and half the story — it says nothing about the account's
// ADJUSTMENTS, which are cascaded away by the database. Those are the eval fees,
// activation fees, resets and payouts: the only record of money that left the
// trader's bank, and the only expense input the Economics tab has.
//
// So this dialog counts first and states both outcomes separately, because they
// are opposite: the trades SURVIVE, the fee rows DO NOT.
//
// ── ⚠ IT OFFERS THE REVERSIBLE ALTERNATIVE ──
//
// Most people reaching for Delete want the account off the list, which is what
// Archive does — reversibly, in one click, from the same menu. Naming it here is
// not a nag; it is the difference between losing a $99 fee record and hiding a
// card.
//
// ── ⚠ WHY NOT A TYPE-THE-NAME CONFIRMATION ──
//
// Considered and rejected. The genuinely irreversible loss is the fee rows, and
// on most accounts that is one row worth tens of dollars — a typing gate would
// be heavier than the consequence, and a gate people meet routinely is one they
// learn to clear without reading. The numbers are the safety here: a dialog that
// says "179 trades" is read, where "are you sure?" is not.
// =============================================================================

import { createPortal } from 'react-dom';
import { money } from '../format';
import type { DeleteImpact } from '../../io/sync/deleteAccount';

export interface DeleteAccountDialogProps {
  name: string;
  /** Null while the count is still running — see `counting`. */
  impact: DeleteImpact | null;
  /** The count failed. The delete is BLOCKED, not offered optimistically. */
  countError: string | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onArchiveInstead: () => void;
  onConfirm: () => void;
}

export function DeleteAccountDialog(
  { name, impact, countError, busy, error, onCancel, onArchiveInstead, onConfirm }:
  DeleteAccountDialogProps,
) {
  const counting = !impact && !countError;

  return createPortal(
    <div className="modal-scrim" role="alertdialog" aria-modal="true"
      aria-label={`Delete ${name}`}>
      <div className="modal">
        <header className="modal-head">
          <b>Delete “{name}”?</b>
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>×</button>
        </header>

        <div className="modal-body dad-body">
          {counting ? <p className="muted">Checking what this would affect…</p> : null}

          {/* ⚠ A FAILED COUNT BLOCKS THE DELETE. "0 fee records" and "we could
              not check" look identical in a dialog, and the first is a promise
              the second cannot make. */}
          {countError ? (
            <p className="red">
              {countError} Nothing has been deleted — the check has to succeed
              first, because this dialog’s whole job is telling you what you
              would lose.
            </p>
          ) : null}

          {impact ? (
            <>
              <ul className="dad-list">
                <li>
                  {impact.trades === 0 ? (
                    <>No trades are on this account.</>
                  ) : (
                    <>
                      <b>{impact.trades} trade{impact.trades === 1 ? '' : 's'}</b> stay in your
                      book but become <b>unassigned</b>. They keep their P&amp;L, and you can
                      re-assign them from the Trades tab.
                    </>
                  )}
                </li>

                {/* 🚨 THE ONE BETA DOES NOT MENTION. */}
                {impact.adjustments > 0 ? (
                  <li className="dad-loss">
                    <b>
                      {impact.adjustments} fee or payout record
                      {impact.adjustments === 1 ? '' : 's'} worth{' '}
                      {money(impact.adjustmentValue, 0)} {impact.adjustments === 1 ? 'is' : 'are'}{' '}
                      deleted for good.
                    </b>{' '}
                    That is the only place this money is recorded, so your lifetime spend
                    on the Economics tab drops by that much and cannot be recovered.
                  </li>
                ) : (
                  <li>No fees or payouts are recorded against it.</li>
                )}
              </ul>

              <p className="muted dad-alt">
                This cannot be undone. If you only want it off the list,{' '}
                <button type="button" className="linklike" onClick={onArchiveInstead}>
                  archive it instead
                </button>{' '}
                — that is reversible, and it also stops any monthly fee accruing.
              </p>
            </>
          ) : null}

          {error ? <p className="red dad-err">{error}</p> : null}
        </div>

        <footer className="modal-foot">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <div className="grow" />
          {/* ⚠ Disabled until the count lands. Confirming a delete whose impact
              is unknown is the thing this dialog exists to prevent. */}
          <button
            type="button" className="btn danger"
            disabled={busy || !impact}
            onClick={onConfirm}
          >
            {busy ? 'Deleting…' : 'Delete account'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
